import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataPackage, DataPackageServiceConfig, PackageStatus, PagedResponse } from './data-package-service.types';
import { FileType } from '@bioconnect-ui/structured-data-service';


@Injectable({
  providedIn: 'root',
})


export class DataPackageService {
  constructor(private httpClient: HttpClient,
    @Inject('dataPackageServiceConfig') private dataPackageServiceConfig: DataPackageServiceConfig) { }

  // Maximum size for a file to be inlcuded in package download (b)
  MAX_DOWNLOAD_FILE_SIZE = 5242880;
  
  private restServer = this.dataPackageServiceConfig.restServer;
  
  /**
   * Create a data package
   */
  createDataPackage(userName: string, description: string, fileIds: number[], downloadFileIds: number[]): Observable<DataPackage> {
    return this.httpClient
      .post<DataPackage>(
        `${this.restServer}/data_package/package/`,
        {
          name: 'name',
          user_name: userName,
          description: description,
          created: Date.now(),
          updated: Date.now(),
          files: fileIds,
          download_files: downloadFileIds
        }
      )
  }

  /**
   * Update a data package
   */
  updateDataPackage(dataPackage: DataPackage): Observable<DataPackage> {
    return this.httpClient.patch<DataPackage>(
      `${this.restServer}/data_package/package/${dataPackage.id}/`,
      dataPackage);
  }

  /**
   * Prepare a data package for download
   */
  generateDataPackageFile(packageId: number) {
    this.httpClient.get<DataPackage>(`${this.restServer}/data_package/package/${packageId}/generate/`).subscribe();
  }

  /**
   * Exchange model pk values for file ids to support package generation
   */
  getFileIds(model: string, ids: number[]): Observable<number[]> {
    if (ids.length) {
      const requestBody = new HttpParams()
        .set(model.concat('_ids'), ids.toString());
      return this.httpClient
        .post<number[]>(`${this.restServer}/metadata_repository/${model}/get_file_ids/`,
          requestBody)
    } else {
      return of([]);
    }
  }

  /**
   * Get the total size for a list of files by file ids
   */
  getFileSizeTotalByFileIds(file_ids: number[]): Observable<number> {
    const requestBody = new HttpParams()
      .set('file_ids', file_ids.toString());

    return this.httpClient.post<any>(`${this.restServer}/core_files/stored_file/get_total_size/`, requestBody)
      .pipe(map(result => <number>result.total_size));
  }

  /**
   * Get the total size for a list of files by package id
   */
  getFileSizeTotalByPackageId(package_id: number): Observable<number> {
    const requestBody = new HttpParams()
      .set('package_id', package_id);

    return this.httpClient.post<any>(`${this.restServer}/core_files/stored_file/get_total_size/`, requestBody)
      .pipe(map(result => <number>result.total_size));
  }

  /**
   * Get the status of a single data package
   */
  getDataPackageStatus(id: number): Observable<PackageStatus> {
    return this.httpClient.get<DataPackage>(
      `${this.restServer}/data_package/package/${id}/`
    ).pipe(map((result) => {
      const dataPackage = <DataPackage>result;
      return dataPackage.status
    }))
  }

  /**
   * Get a paged set of packages for a user based on user name
   */
  getDataPackagesByUser(userName: string, filter: string, pageNumber: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/data_package/package/?user_name=${userName}&filter=${filter}&page=${pageNumber}`)
      .pipe(map(result => {
        return <PagedResponse>result;
      }));
  }

  /**
   * Get the details for a single package based on id
   */
  getPackageDetails(id: number) {
    return this.httpClient.get<DataPackage>(`${this.restServer}/data_package/package/${id}/`)
      .pipe(map(result => {
        return <DataPackage>result;
      }));
  }

  /**
   * Get a paged set of files for a data pacage based on package id
   */
  getPackageFiles(packageId: number, pageNumber: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/data_package/package/package_files/?package_id=${packageId}&page=${pageNumber}`)
      .pipe(map(result => {
        return <PagedResponse>result;
      }));
  }

  /**
   * Get a paged set of files for a study
   */
    getPackageFilesByStudy(studyId: number, pageNumber: number): Observable<PagedResponse> {
      return this.httpClient.get(`${this.restServer}/data_package/package/package_files/?study_id=${studyId}&page=${pageNumber}`)
        .pipe(map(result => {
          return <PagedResponse>result;
        }));
    }

  /**
   * Get a paged set of files for an assay
   */
    getPackageFilesByAssay(studyId: number, pageNumber: number): Observable<PagedResponse> {
      return this.httpClient.get(`${this.restServer}/data_package/package/package_files/?assay_id=${studyId}&page=${pageNumber}`)
        .pipe(map(result => {
          return <PagedResponse>result;
        }));
    }

  /**
   * Get a paged set of files for a set of file ids
   */
  getPackageFilesByFileIds(file_ids: number[], pageSize: number): Observable<PagedResponse> {
    const requestBody = new HttpParams()
      .set('file_ids', file_ids.toString());

    return this.httpClient.post<any>(`${this.restServer}/data_package/package/package_files/?page_size=${pageSize}`, requestBody)
      .pipe(map(result => {
        return <PagedResponse>result;
      }
    ));
  }

  /**
   * Get a file type by id
   */
   getFileType(fileTypeId: number): Observable<FileType> {
    return this.httpClient.get<FileType>(
      `${this.restServer}/data_package/file_type/${fileTypeId}/`
    );
  }

  /**
   * Get a paged set of file types for a data pacage based on package id
   */
  getPackageFileTypes(packageId: number, pageNumber: number): Observable<PagedResponse> {
    return this.httpClient.get(`${this.restServer}/data_package/file_type/package_file_types/?package_id=${packageId}&page=${pageNumber}`)
      .pipe(map(result => {
        return <PagedResponse>result;
      }));
  }

  /**
   * Create a data package given a data package object
   */
  addDataPackage(dataPackage: DataPackage) {
    this.httpClient
      .post(
        `${this.restServer}/data_package/package/`,
        dataPackage
      ).subscribe();
  }

  /**
   * Delete an existing data package
   */
  deleteDataPackage(id: number) {
    return this.httpClient.delete(
      `${this.restServer}/data_package/package/${id}/`
    );
  }

  /**
   * Initiate download of a package based on package id
   */
  downloadDataPackage(packageId: number): any {
    return this.httpClient
      .get<string>(
        `${this.restServer}/data_package/package/${packageId}/get_download_url/`
      )
      .pipe(
        switchMap((url: any) => {
          return this.httpClient.get(url.location, { responseType: 'blob'});
        })
      );
  }
}
